import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { DelegationSummary, UpdateRequest } from '../types/delegation';
import {
  getDelegationDetail,
  getDelegationUpdateRequests,
  completeDelegation,
  recallDelegation,
  requestDelegationUpdate,
  respondToUpdate,
} from '../api/delegationsApi';

type ActionMode = 'none' | 'complete' | 'recall' | 'request-update' | 'respond';

const DelegationDetail: React.FC = () => {
  const { guid } = useParams<{ guid: string }>();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const currentUserId = accounts[0]?.localAccountId ?? '';

  const [delegation, setDelegation] = useState<DelegationSummary | null>(null);
  const [updateRequests, setUpdateRequests] = useState<UpdateRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Action state
  const [actionMode, setActionMode] = useState<ActionMode>('none');
  const [actionInput, setActionInput] = useState('');
  const [actionDueBy, setActionDueBy] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const loadData = (delegationGuid: string) =>
    Promise.all([
      getDelegationDetail(delegationGuid),
      getDelegationUpdateRequests(delegationGuid),
    ]);

  useEffect(() => {
    if (!guid) {
      setError('Invalid delegation ID.');
      setLoading(false);
      return;
    }

    loadData(guid)
      .then(([d, reqs]) => {
        setDelegation(d);
        setUpdateRequests(reqs);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [guid]);

  if (loading) return <div style={{ padding: '2rem' }}>Loading delegation...</div>;
  if (error) return <div style={{ padding: '2rem', color: 'red' }}>{error}</div>;
  if (!delegation) return <div style={{ padding: '2rem' }}>Delegation not found.</div>;

  const isDelegator = currentUserId === delegation.delegatedByUserId;
  const isDelegate = currentUserId === delegation.delegatedToUserId;
  const isActive = delegation.status === 'Active';
  const pendingUpdate = updateRequests.find((r) => r.status === 'Pending') ?? null;
  const hasPendingUpdate = pendingUpdate !== null;

  // ── Action handlers ────────────────────────────────────────────────────────

  const handleComplete = async () => {
    setActionBusy(true);
    setActionError(null);
    try {
      await completeDelegation(delegation.delegationId);
      navigate('/delegations');
    } catch (err: any) {
      setActionError(err.message);
    } finally {
      setActionBusy(false);
    }
  };

  const handleRecall = async () => {
    if (!actionInput.trim()) { setActionError('Recall reason is required.'); return; }
    setActionBusy(true);
    setActionError(null);
    try {
      await recallDelegation(delegation.delegationId, actionInput.trim());
      navigate('/delegations');
    } catch (err: any) {
      setActionError(err.message);
    } finally {
      setActionBusy(false);
    }
  };

  const handleRequestUpdate = async () => {
    setActionBusy(true);
    setActionError(null);
    try {
      await requestDelegationUpdate(
        delegation.delegationId,
        actionInput.trim() || undefined,
        actionDueBy || undefined
      );
      // Refresh update requests list
      const reqs = await getDelegationUpdateRequests(guid!);
      setUpdateRequests(reqs);
      setActionMode('none');
      setActionInput('');
      setActionDueBy('');
    } catch (err: any) {
      setActionError(err.message);
    } finally {
      setActionBusy(false);
    }
  };

  const handleRespond = async () => {
    if (!actionInput.trim()) { setActionError('Response text is required.'); return; }
    if (!pendingUpdate) return;
    setActionBusy(true);
    setActionError(null);
    try {
      await respondToUpdate(pendingUpdate.updateRequestId, actionInput.trim());
      const reqs = await getDelegationUpdateRequests(guid!);
      setUpdateRequests(reqs);
      setActionMode('none');
      setActionInput('');
    } catch (err: any) {
      setActionError(err.message);
    } finally {
      setActionBusy(false);
    }
  };

  const cancelAction = () => {
    setActionMode('none');
    setActionInput('');
    setActionDueBy('');
    setActionError(null);
  };

  // ── Styles ─────────────────────────────────────────────────────────────────

  const btnBase: React.CSSProperties = {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.875rem',
  };
  const btnPrimary: React.CSSProperties = { ...btnBase, background: '#2563eb', color: '#fff' };
  const btnDanger: React.CSSProperties = { ...btnBase, background: '#dc2626', color: '#fff' };
  const btnSecondary: React.CSSProperties = { ...btnBase, background: '#e5e7eb', color: '#374151' };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
      <button onClick={() => navigate('/delegations')} style={btnSecondary}>
        ← Back to inbox
      </button>

      <h1 style={{ marginBottom: '0.5rem', marginTop: '1.5rem' }}>
        {delegation.sourceObjectType}: {delegation.sourceObjectName || delegation.sourceObjectId}
      </h1>

      {/* Status badges */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        <span style={{
          padding: '0.25rem 0.75rem', borderRadius: '999px', fontWeight: 600, fontSize: '0.875rem',
          background: delegation.status === 'Active' ? '#d1fae5' : '#f3f4f6',
          color: delegation.status === 'Active' ? '#065f46' : '#374151',
        }}>
          {delegation.status}
        </span>
        {delegation.isOverdue && (
          <span style={{
            padding: '0.25rem 0.75rem', borderRadius: '999px', fontWeight: 600, fontSize: '0.875rem',
            background: '#fee2e2', color: '#991b1b',
          }}>
            Overdue
          </span>
        )}
      </div>

      {/* Delegation fields */}
      <dl style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '0.5rem 1rem', marginBottom: '2rem' }}>
        <dt style={{ fontWeight: 600, color: '#6b7280' }}>Delegated by</dt>
        <dd>{delegation.delegatedByDisplayName || delegation.delegatedByUserId}</dd>

        <dt style={{ fontWeight: 600, color: '#6b7280' }}>Delegated to</dt>
        <dd>{delegation.delegatedToDisplayName || delegation.delegatedToUserId}</dd>

        {delegation.dueDate && (
          <>
            <dt style={{ fontWeight: 600, color: '#6b7280' }}>Due date</dt>
            <dd>{new Date(delegation.dueDate).toLocaleDateString()}</dd>
          </>
        )}

        <dt style={{ fontWeight: 600, color: '#6b7280' }}>Visibility</dt>
        <dd>{delegation.visibilityMode}</dd>

        <dt style={{ fontWeight: 600, color: '#6b7280' }}>Created</dt>
        <dd>{new Date(delegation.createDt).toLocaleDateString()}</dd>

        {delegation.note && (
          <>
            <dt style={{ fontWeight: 600, color: '#6b7280' }}>Note</dt>
            <dd style={{ whiteSpace: 'pre-wrap' }}>{delegation.note}</dd>
          </>
        )}

        {delegation.status === 'Recalled' && delegation.recallReason && (
          <>
            <dt style={{ fontWeight: 600, color: '#6b7280' }}>Recall reason</dt>
            <dd>{delegation.recallReason}</dd>
          </>
        )}
      </dl>

      {/* ── Update requests ──────────────────────────────────────────── */}
      {updateRequests.length > 0 && (
        <section style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.75rem' }}>Update Requests</h2>
          {updateRequests.map((req) => (
            <div key={req.updateRequestId} style={{
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '0.75rem',
              background: req.status === 'Pending' ? '#fffbeb' : '#f9fafb',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151' }}>
                  {req.status === 'Pending' ? '⏳ Awaiting response' : `✓ ${req.status}`}
                </span>
                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {new Date(req.requestedAt).toLocaleDateString()}
                  {req.isOverdue && (
                    <span style={{ marginLeft: '0.5rem', color: '#dc2626', fontWeight: 600 }}>Overdue</span>
                  )}
                </span>
              </div>
              {req.message && (
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.9rem', color: '#1f2937' }}>{req.message}</p>
              )}
              {req.dueBy && (
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#6b7280' }}>
                  Due by: {new Date(req.dueBy).toLocaleDateString()}
                </p>
              )}
              {req.responseText && (
                <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#f0fdf4', borderRadius: '6px' }}>
                  <p style={{ margin: '0 0 0.25rem', fontWeight: 600, fontSize: '0.8rem', color: '#065f46' }}>Response</p>
                  <p style={{ margin: 0, fontSize: '0.875rem' }}>{req.responseText}</p>
                  {req.respondedAt && (
                    <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
                      {new Date(req.respondedAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </section>
      )}

      {/* ── Actions ──────────────────────────────────────────────────── */}
      {(isDelegator || isDelegate) && isActive && (
        <section style={{ marginTop: '1rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.75rem' }}>Actions</h2>

          {actionError && (
            <p style={{ color: '#dc2626', marginBottom: '0.75rem', fontSize: '0.875rem' }}>{actionError}</p>
          )}

          {actionMode === 'none' && (
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              {isDelegate && (
                <button style={btnPrimary} onClick={() => setActionMode('complete')}>
                  Mark Complete
                </button>
              )}
              {isDelegator && !hasPendingUpdate && (
                <button style={btnSecondary} onClick={() => setActionMode('request-update')}>
                  Request Update
                </button>
              )}
              {isDelegate && hasPendingUpdate && (
                <button style={btnSecondary} onClick={() => setActionMode('respond')}>
                  Respond to Update Request
                </button>
              )}
              {isDelegator && (
                <button style={btnDanger} onClick={() => setActionMode('recall')}>
                  Recall
                </button>
              )}
            </div>
          )}

          {/* Complete confirm */}
          {actionMode === 'complete' && (
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <span style={{ fontSize: '0.875rem' }}>Mark this delegation as complete?</span>
              <button style={btnPrimary} onClick={handleComplete} disabled={actionBusy}>
                {actionBusy ? 'Saving...' : 'Confirm'}
              </button>
              <button style={btnSecondary} onClick={cancelAction}>Cancel</button>
            </div>
          )}

          {/* Recall form */}
          {actionMode === 'recall' && (
            <div style={{ maxWidth: '480px' }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.4rem' }}>
                Recall reason <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <textarea
                value={actionInput}
                onChange={(e) => setActionInput(e.target.value)}
                rows={3}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', marginBottom: '0.75rem', boxSizing: 'border-box' }}
                placeholder="Why are you recalling this delegation?"
              />
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button style={btnDanger} onClick={handleRecall} disabled={actionBusy}>
                  {actionBusy ? 'Saving...' : 'Recall Delegation'}
                </button>
                <button style={btnSecondary} onClick={cancelAction}>Cancel</button>
              </div>
            </div>
          )}

          {/* Request update form */}
          {actionMode === 'request-update' && (
            <div style={{ maxWidth: '480px' }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.4rem' }}>
                Message (optional)
              </label>
              <textarea
                value={actionInput}
                onChange={(e) => setActionInput(e.target.value)}
                rows={3}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', marginBottom: '0.75rem', boxSizing: 'border-box' }}
                placeholder="What would you like an update on?"
              />
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.4rem' }}>
                Due by (optional)
              </label>
              <input
                type="date"
                value={actionDueBy}
                onChange={(e) => setActionDueBy(e.target.value)}
                style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', marginBottom: '0.75rem' }}
              />
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button style={btnPrimary} onClick={handleRequestUpdate} disabled={actionBusy}>
                  {actionBusy ? 'Sending...' : 'Send Request'}
                </button>
                <button style={btnSecondary} onClick={cancelAction}>Cancel</button>
              </div>
            </div>
          )}

          {/* Respond form */}
          {actionMode === 'respond' && pendingUpdate && (
            <div style={{ maxWidth: '480px' }}>
              {pendingUpdate.message && (
                <div style={{ background: '#fffbeb', padding: '0.75rem', borderRadius: '6px', marginBottom: '0.75rem', fontSize: '0.875rem' }}>
                  <strong>Request:</strong> {pendingUpdate.message}
                </div>
              )}
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.4rem' }}>
                Your response <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <textarea
                value={actionInput}
                onChange={(e) => setActionInput(e.target.value)}
                rows={4}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', marginBottom: '0.75rem', boxSizing: 'border-box' }}
                placeholder="Provide your status update..."
              />
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button style={btnPrimary} onClick={handleRespond} disabled={actionBusy}>
                  {actionBusy ? 'Sending...' : 'Submit Response'}
                </button>
                <button style={btnSecondary} onClick={cancelAction}>Cancel</button>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default DelegationDetail;

